import { FaPencilAlt, FaPlus, FaTimes } from 'react-icons/fa';
import { CustomModal } from '../../Modal/CustomModal';

const LedgerGroupModal = ({
    groupModal,
    closeGroupModal,
    groupModalLedgers,
    groupModalTotals,
    formatSalesValue,
    addingLedger,
    newLedgerName,
    setNewLedgerName,
    newLedgerTotal,
    setNewLedgerTotal,
    creatingLedger,
    handleStartAddLedger,
    handleCancelAddLedger,
    handleSaveNewLedger,
    newLedgerSubLedgerDrafts,
    draftSubName,
    setDraftSubName,
    draftSubTotal,
    setDraftSubTotal,
    handleAddDraftSubLedger,
    handleRemoveDraftSubLedger,
    renamingLedgerId,
    renameValue,
    setRenameValue,
    openRenameInline,
    closeRenameInline,
    handleSaveRename,
    removeLedgerFromGroup,
    expandedSubLedgerFor,
    toggleSubLedgers,
    getSubLedgerTotals,
    addingSubLedgerFor,
    newSubLedgerName,
    setNewSubLedgerName,
    newSubLedgerTotal,
    setNewSubLedgerTotal,
    creatingSubLedger,
    handleStartAddSubLedger,
    closeSubLedgerForm,
    handleSaveSubLedger,
}) => {
    const hasPendingDraftSubLedger = draftSubName.trim().length > 0;

    return (
        <CustomModal show={!!groupModal} onHide={closeGroupModal} size="md" centered scrollable>
            <CustomModal.Header closeButton onHide={closeGroupModal}>
                <CustomModal.Title className="text-base flex items-center gap-2">
                    {groupModal?.group}
                    <span className="text-xs bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 text-gray-600 font-medium">
                        {groupModalLedgers.length}
                    </span>
                </CustomModal.Title>
            </CustomModal.Header>

            <CustomModal.Body className="space-y-1.5">
                {!addingLedger && (
                    <button
                        type="button"
                        onClick={handleStartAddLedger}
                        className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 border border-dashed border-blue-300 hover:border-blue-400 rounded-md py-2 mb-1.5 transition"
                    >
                        <FaPlus size={12} />
                        Add ledger
                    </button>
                )}

                {addingLedger && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 mb-1.5 flex flex-col gap-1.5">
                        <input
                            type="text"
                            value={newLedgerName}
                            onChange={(e) => setNewLedgerName(e.target.value)}
                            placeholder="Ledger name"
                            autoFocus
                            className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                        />
                        <input
                            type="number"
                            value={newLedgerTotal}
                            onChange={(e) => setNewLedgerTotal(e.target.value)}
                            placeholder="Total value"
                            className="px-2 py-1.5 text-sm w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                        />

                        {(newLedgerSubLedgerDrafts.length > 0 || hasPendingDraftSubLedger) && (
                            <div className="flex flex-col gap-1">
                                {newLedgerSubLedgerDrafts.length > 0 && (
                                    <div className="text-xs font-medium text-gray-600 px-0.5">
                                        Sub-ledgers to add
                                    </div>
                                )}
                                {newLedgerSubLedgerDrafts.map((d) => (
                                    <div
                                        key={d.id}
                                        className="flex items-center justify-between bg-white border border-gray-200 rounded px-2 py-1"
                                    >
                                        <span className="text-xs text-gray-700">
                                            {d.name} — {formatSalesValue(parseFloat(d.total) || 0)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveDraftSubLedger(d.id)}
                                            className="p-1 rounded-full bg-gray-50 text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                                        >
                                            <FaTimes size={10} />
                                        </button>
                                    </div>
                                ))}

                                {(() => {
                                    const draftParentTotal = parseFloat(newLedgerTotal) || 0;
                                    const pendingTotal = hasPendingDraftSubLedger
                                        ? (parseFloat(draftSubTotal) || 0)
                                        : 0;
                                    const draftSubTotalSum =
                                        newLedgerSubLedgerDrafts.reduce(
                                            (sum, d) => sum + (parseFloat(d.total) || 0),
                                            0
                                        ) + pendingTotal;
                                    const draftRemaining = draftParentTotal - draftSubTotalSum;
                                    const draftIsBalanced = Math.abs(draftRemaining) < 0.005;

                                    return (
                                        <div
                                            className={`text-xs font-medium px-0.5 ${draftIsBalanced ? "text-green-600" : "text-orange-600"
                                                }`}
                                        >
                                            {draftIsBalanced
                                                ? "Fully allocated to sub-ledgers"
                                                : `Remaining to allocate — ${formatSalesValue(Math.max(draftRemaining, 0))}`}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        <div className="flex items-center gap-1.5">
                            <input
                                type="text"
                                value={draftSubName}
                                onChange={(e) => setDraftSubName(e.target.value)}
                                placeholder="Sub-ledger name (optional)"
                                className="px-2 py-1 text-xs w-full border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                            />
                            <input
                                type="number"
                                value={draftSubTotal}
                                onChange={(e) => setDraftSubTotal(e.target.value)}
                                placeholder="Total"
                                className="px-2 py-1 text-xs w-24 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                            />
                            <button
                                type="button"
                                onClick={handleAddDraftSubLedger}
                                title="Add another sub-ledger row"
                                className="px-2 py-1 rounded font-medium bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 text-xs transition whitespace-nowrap"
                            >
                                + Add
                            </button>
                        </div>

                        <div className='flex items-center gap-2'>
                            <button
                                type="button"
                                onClick={handleCancelAddLedger}
                                disabled={creatingLedger}
                                className="px-2.5 py-1.5 rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 text-xs transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveNewLedger}
                                disabled={creatingLedger}
                                className="flex-1 px-2.5 py-1.5 rounded font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs transition disabled:opacity-50"
                            >
                                {creatingLedger ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                )}

                {groupModalLedgers.map((ledger) => {
                    const canManageSubLedgers = ledger.isManual || ledger.isExisting;
                    const subLedgers = ledger.subLedgers ?? [];
                    const subTotal = getSubLedgerTotals(ledger);
                    const parentTotal = parseFloat(ledger.totalValue ?? ledger.debit) || 0;
                    const remaining = parentTotal - subTotal;
                    const isBalanced = Math.abs(remaining) < 0.005;
                    const isSubExpanded = expandedSubLedgerFor === ledger.id;
                    const showAllocationText = ledger.isManual && !ledger.isExisting;

                    return (
                        <div key={ledger.id}>
                            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
                                <div
                                    className={`text-sm flex-1 ${!ledger.isExisting && !ledger.isManual ? "cursor-pointer group" : ""}`}
                                    onClick={() => {
                                        if (ledger.isExisting || ledger.isManual) return;
                                        openRenameInline(ledger.id, ledger.name);
                                    }}
                                >
                                    <div
                                        className={`font-medium text-gray-800 tracking-wide flex items-center gap-2.5 ${!ledger.isExisting && !ledger.isManual ? "hover:text-blue-600" : ""
                                            }`}
                                    >
                                        {ledger.name}
                                        {!ledger.isExisting && !ledger.isManual && (
                                            <FaPencilAlt size={13} className="text-gray-500 group-hover:text-blue-500" />
                                        )}
                                    </div>
                                    <div className="text-gray-600 text-xs font-medium mt-1">
                                        {canManageSubLedgers
                                            ? `Total: ${formatSalesValue(parentTotal)}`
                                            : `Dr: ${formatSalesValue(ledger.debit)} · Cr: ${formatSalesValue(ledger.credit)}`}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    {canManageSubLedgers && (
                                        <button
                                            type="button"
                                            onClick={() => toggleSubLedgers(ledger.id)}
                                            className={`text-xs font-medium rounded-full px-2 py-1 transition ${isSubExpanded
                                                ? "bg-blue-100 text-blue-800"
                                                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                                                }`}
                                        >
                                            Sub-ledgers{subLedgers.length > 0 ? ` (${subLedgers.length})` : ""}
                                        </button>
                                    )}
                                    {!ledger.isExisting && !ledger.isManual && (
                                        <button
                                            type="button"
                                            onClick={() => removeLedgerFromGroup(ledger.id)}
                                            className="p-1.5 rounded-full bg-white text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                                        >
                                            <FaTimes size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {renamingLedgerId === ledger.id && (
                                <div className="mt-1 flex items-center gap-1.5 bg-white border border-gray-200 rounded-md p-1.5">
                                    <input
                                        type="text"
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleSaveRename();
                                            if (e.key === "Escape") closeRenameInline();
                                        }}
                                        autoFocus
                                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSaveRename}
                                        className="px-2.5 py-1.5 rounded font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs transition"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={closeRenameInline}
                                        className="px-2.5 py-1.5 rounded font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs transition"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}

                            {canManageSubLedgers && isSubExpanded && (
                                <div className="mt-1.5 ml-1.5 pl-2 border-l-2 border-gray-200 space-y-1.5">
                                    {showAllocationText && (
                                        <div className={`text-xs font-medium px-1 ${isBalanced ? "text-green-600" : "text-orange-600"}`}>
                                            {isBalanced
                                                ? "Fully allocated to sub-ledgers"
                                                : `Remaining to allocate — ${formatSalesValue(Math.max(remaining, 0))}`}
                                        </div>
                                    )}

                                    {subLedgers.map((sub) => (
                                        <div
                                            key={sub.id}
                                            className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-2 py-1.5"
                                        >
                                            <div className="text-sm">
                                                <div className="font-medium text-gray-800">{sub.name}</div>
                                                <div className="text-gray-500 text-xs font-medium mt-0.5">
                                                    Total: {formatSalesValue(sub.totalValue)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {subLedgers.length === 0 && (
                                        <div className="text-xs text-gray-400 px-1">No sub-ledgers yet</div>
                                    )}

                                    {addingSubLedgerFor === ledger.id ? (
                                        <div className="bg-blue-50 border border-blue-200 rounded-md p-2 flex flex-col gap-1.5">
                                            <input
                                                type="text"
                                                value={newSubLedgerName}
                                                onChange={(e) => setNewSubLedgerName(e.target.value)}
                                                placeholder="Sub-ledger name"
                                                autoFocus
                                                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                                            />
                                            <div className='flex items-center gap-2'>
                                                <input
                                                    type="number"
                                                    value={newSubLedgerTotal}
                                                    onChange={(e) => setNewSubLedgerTotal(e.target.value)}
                                                    placeholder="Total value"
                                                    className="px-2 py-1 w-full text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={closeSubLedgerForm}
                                                    disabled={creatingSubLedger}
                                                    className="px-2.5 py-1.5 rounded font-medium bg-red-100 text-red-700 hover:bg-red-200 text-xs transition disabled:opacity-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveSubLedger(ledger)}
                                                    disabled={creatingSubLedger}
                                                    className="px-2.5 py-1.5 rounded font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs transition disabled:opacity-50"
                                                >
                                                    {creatingSubLedger ? "Saving..." : "Save"}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => handleStartAddSubLedger(ledger.id)}
                                            className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 transition px-1"
                                        >
                                            <FaPlus size={11} />
                                            Add sub-ledger
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {groupModalLedgers.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-6">No ledgers yet</div>
                )}
            </CustomModal.Body>

            <CustomModal.Footer>
                <div className="flex justify-end gap-6 text-sm font-semibold text-gray-800">
                    <span>Total Debit: {formatSalesValue(groupModalTotals.debit)}</span>
                    <span>Total Credit: {formatSalesValue(groupModalTotals.credit)}</span>
                </div>
            </CustomModal.Footer>
        </CustomModal>
    );
};

export default LedgerGroupModal;